import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getEnquiryStats,
  getTripStats,
  getKycStats,
  getSupportTicketStats,
  getCustomerActivityStats,
  getTransporterActivityStats,
  getDriverAvailabilityStats,
  getTransporterVehicleAvailabilityStats,
  getpaymentStats,
} from "../../../api/Admin/dashboard";
import useToast from "../../../hooks/useToast";
import styled from "@emotion/styled";

const StatusCard = ({ title, data, navigation }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ breakInside: 'avoid', mb: 3 }}>
      <Paper elevation={4} sx={{
        p: 2.5,
        borderRadius: 8,
        background: '#fff',
        boxShadow: '0 2px 12px rgba(87,73,100,0.08)',
        border: '1px solid #e0e0e0',
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', mb: 1.2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#222', letterSpacing: 0.2 }}>
            {title}
          </Typography>
          <StyledCardButton
            variant="contained"
            color="success"
            onClick={() => navigate(navigation)}
            sx={{ fontWeight: 600, fontSize: 13, borderRadius: 3, boxShadow: '0 2px 6px rgba(12,127,68,0.10)', py: 0.7, px: 2, background: '#0C7F44', color: '#fff' }}
          >
            View
          </StyledCardButton>
        </Box>
        <List>
          {Object.keys(data).map((key) => (
            <ListItem
              key={key}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                bgcolor: "#f9f9fb",
                mb: 0.5,
                fontSize: 13,
                borderRadius: 4,
                boxShadow: '0 1px 2px rgba(87,73,100,0.04)',
                p: '7px 14px',
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography fontSize="13px" color="#222" sx={{ fontWeight: 500 }}>{key}</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#0C7F44' }}>
                  {data[key]}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

const Dashboard = () => {
  const [enquiryStats, setEnquiryStats] = useState({});
  const [tripStats, setTripStats] = useState({});
  const [kycStats, setKycStats] = useState({});
  const [supportStats, setSupportStats] = useState({});
  const [customerActivityStats, setCustomerActivityStats] = useState({});
  const [transporterActivityStats, setTransporterActivityStats] = useState({});
  const [driverAvailabilityStats, setDriverAvailabilityStats] = useState({});
  const [
    transporterVehicleAvailabilityStats,
    setTransporterVehicleAvailabilityStats,
  ] = useState({});
  const [paymentStats, setPaymentStats] = useState({});
  const { handleOpenToast, ToastMessage } = useToast();

  const getEnquiryStat = async () => {
    try {
      const response = await getEnquiryStats();
      setEnquiryStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching enquiry count", "error");
    }
  };

  const getTripStat = async () => {
    try {
      const response = await getTripStats();
      setTripStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching trip count", "error");
    }
  };

  const getKycStat = async () => {
    try {
      const response = await getKycStats();
      setKycStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching kyc count", "error");
    }
  };

  const getSupportStat = async () => {
    try {
      const response = await getSupportTicketStats();
      setSupportStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching support count", "error");
    }
  };

  const getCustomerActivityStat = async () => {
    try {
      const response = await getCustomerActivityStats();
      setCustomerActivityStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching customer count", "error");
    }
  };

  const getTransporterActivityStat = async () => {
    try {
      const response = await getTransporterActivityStats();
      setTransporterActivityStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching transporter count", "error");
    }
  };

  const getDriverAvailabilityStat = async () => {
    try {
      const response = await getDriverAvailabilityStats();
      setDriverAvailabilityStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching driver count", "error");
    }
  };

  const getTransporterVehicleAvailabilityStat = async () => {
    try {
      const response = await getTransporterVehicleAvailabilityStats();
      setTransporterVehicleAvailabilityStats(response.data.data);
    } catch (error) {
      handleOpenToast("Error while fetching truck count", "error");
    }
  };

  const getPaymentStats = async () => {
    try {
      const response = await getpaymentStats();
      setPaymentStats(response.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const statistics = {
    enquiry: {
      "Total Enquiries": enquiryStats?.total || 0,
      "No Service": enquiryStats?.noService || 0,
      Quoted: enquiryStats?.quoted || 0,
      "Not Quoted": enquiryStats?.notQuoted || 0,
      "Converted to Trip": enquiryStats?.convertedToTrip || 0,
      "Expired Enquiry": enquiryStats?.expired || 0,
      "Closed Enquiry": enquiryStats?.closed || 0,
      Rejected: enquiryStats?.rejected || 0,
    },
    trips: {
      "Total Trips": tripStats?.total || 0,
      Upcoming: tripStats?.upcoming || 0,
      "In Transit": tripStats?.inTransit || 0,
      Completed: tripStats?.completed || 0,
      Canceled: tripStats?.cancelled || 0,
    },
    kyc: {
      "Total KYC Approvals": kycStats?.total || 0,
      Approved: kycStats?.kycApproved || 0,
      Pending: kycStats?.kycPending || 0,
      Incomplete: kycStats?.kycIncomplete || 0,
      Rejected: kycStats?.kycRejected || 0,
    },
    support: {
      "Total Support Tickets": supportStats?.total || 0,
      "High Priority": supportStats?.high || 0,
      "Medium Priority": supportStats?.medium || 0,
      "Low Priority": supportStats?.low || 0,
    },
    customer: {
      "Total Customers": customerActivityStats?.total || 0,
      Active: customerActivityStats?.active || 0,
      Inactive: customerActivityStats?.inactive || 0,
      Blocked: customerActivityStats?.blocked || 0,
    },
    transporter: {
      "Total Transporters": transporterActivityStats?.total || 0,
      Active: transporterActivityStats?.active || 0,
      Inactive: transporterActivityStats?.inactive || 0,
      Blocked: transporterActivityStats?.blocked || 0,
    },
    driver: {
      "Total Drivers": driverAvailabilityStats?.total || 0,
      Available: driverAvailabilityStats?.available || 0,
      Unavailable: driverAvailabilityStats?.unavailable || 0,
      "In Transit": driverAvailabilityStats?.inTransit || 0,
      Invited: driverAvailabilityStats?.invited || 0,
    },
    truck: {
      "Total Trucks": transporterVehicleAvailabilityStats?.total || 0,
      Available: transporterVehicleAvailabilityStats?.available || 0,
      Unavailable: transporterVehicleAvailabilityStats?.unavailable || 0,
      "In Transit": transporterVehicleAvailabilityStats?.inTransit || 0,
    },
  };

  const paymentData = {
    receivable: {
      "Total Amount Receivable": paymentStats.totalAmountReceivable,
      "Amount Received": paymentStats.amountReceived,
      "Balance Receivable": paymentStats.balanceReceivable,
    },
    payable: {
      "Total Amount Payable": paymentStats.totalAmountPayable,
      "Amount Paid": paymentStats.amountPaid,
      "Balance Payable": paymentStats.balancePayable,
    },
  };

  useEffect(() => {
    getEnquiryStat();
    getTripStat();
    getKycStat();
    getSupportStat();
    getCustomerActivityStat();
    getTransporterActivityStat();
    getDriverAvailabilityStat();
    getTransporterVehicleAvailabilityStat();
    getPaymentStats();
  }, []);

  const navigate = useNavigate();

  return (
    <Box sx={{ p: 2 }}>
      <ToastMessage />
      <Box
        sx={{
          columnCount: { xs: 1, sm: 2, md: 3 },
          columnGap: 4,
        }}
      >
        <StatusCard title="Enquiry Summary" data={statistics.enquiry} navigation="/admin/app/enquiries" />
        <StatusCard title="Trip Overview" data={statistics.trips} navigation="/admin/app/trips" />
        <StatusCard title="KYC Approval" data={statistics.kyc} navigation="/admin/app/approval" />
        <StatusCard title="Customer" data={statistics.customer} navigation="/admin/app/resources/customers" />
        <StatusCard title="Transporter" data={statistics.transporter} navigation="/admin/app/resources/transporters" />
        <StatusCard title="Driver" data={statistics.driver} navigation="/admin/app/resources/drivers" />
        <StatusCard title="Truck" data={statistics.truck} navigation="/admin/app/resources/trucks" />
        <StatusCard title="Support Summary" data={statistics.support} navigation="/admin/app/support" />
        <StatusCard title="Payment Summary" data={{ ...paymentData.receivable, ...paymentData.payable }} navigation="/admin/app/payments" />
      </Box>
    </Box>
  );
};

export default Dashboard;

const StyledCardButton = styled(Button)(() => ({
  height: 28,
  fontSize: 13,
  fontWeight: "bold",
  borderRadius: "3px",
  boxShadow: "0px 2px 6px rgba(12,127,68,0.10)",
  background: '#0C7F44',
  color: '#fff',
  textTransform: 'none',
  '&:hover': {
    background: '#095c32',
  },
}));
